import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import WebRoundedIcon from '@mui/icons-material/WebRounded';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { manualReportsApi } from '../../services/manualReportsApi';
import './reports.css';

const brand = PRIMARYCOLOR || '#005CC5';

const TYPE_META = {
  external: { label: 'External', icon: PublicRoundedIcon, color: '#16a34a' },
  web: { label: 'Web App', icon: WebRoundedIcon, color: '#2563eb' },
  api: { label: 'API', icon: LanguageRoundedIcon, color: '#0ea5e9' },
  vishing: { label: 'Vishing', icon: AssignmentOutlinedIcon, color: '#f59e0b' },
  darkweb: { label: 'Dark Web', icon: LockOutlinedIcon, color: '#7c3aed' },
  osint: { label: 'OSINT', icon: TravelExploreRoundedIcon, color: brand },
};

const STATUS_META = {
  running: { key: 'running', label: 'Running', color: '#0284c7', icon: AutoAwesomeRoundedIcon },
  complete: { key: 'complete', label: 'Complete', color: '#16a34a', icon: CheckCircleOutlineRoundedIcon },
  failed: { key: 'failed', label: 'Failed', color: '#dc2626', icon: WarningAmberRoundedIcon },
};

const reportRoutes = {
  external: [
    { label: 'Pentest', path: (id) => `/pentest/report/pentest/${id}` },
    { label: 'Executive', path: (id) => `/pentest/report/executive/${id}` },
    { label: 'Recon', path: (id) => `/pentest/report/recon/${id}` },
    { label: 'Attestation', path: (id) => `/pentest/report/attestation/${id}` },
  ],
  web: [
    { label: 'Pentest', path: (id) => `/pentest/web/report/pentest/${id}` },
    { label: 'Executive', path: (id) => `/pentest/web/report/executive/${id}` },
    { label: 'Recon', path: (id) => `/pentest/web/report/recon/${id}` },
    { label: 'Attestation', path: (id) => `/pentest/web/report/attestation/${id}` },
  ],
  api: [
    { label: 'Pentest', path: (id) => `/pentest/api/report/pentest/${id}` },
    { label: 'Executive', path: (id) => `/pentest/api/report/executive/${id}` },
    { label: 'Attestation', path: (id) => `/pentest/api/report/attestation/${id}` },
  ],
  vishing: [
    { label: 'Pentest', path: (id) => `/pentest/vishing/report/pentest/${id}` },
    { label: 'Executive', path: (id) => `/pentest/vishing/report/executive/${id}` },
    { label: 'Attestation', path: (id) => `/pentest/vishing/report/attestation/${id}` },
  ],
  darkweb: [
    { label: 'Pentest', path: (id) => `/pentest/darkweb/report/pentest/${id}` },
    { label: 'Executive', path: (id) => `/pentest/darkweb/report/executive/${id}` },
    { label: 'Attestation', path: (id) => `/pentest/darkweb/report/attestation/${id}` },
  ],
};

const normalizePentestType = (value) => {
  const normalized = String(value || 'external').toLowerCase();
  if (normalized.includes('osint')) return 'osint';
  if (normalized.includes('dark')) return 'darkweb';
  if (normalized.includes('vishing')) return 'vishing';
  if (normalized.includes('api')) return 'api';
  if (normalized.includes('web')) return 'web';
  return 'external';
};

const normalizeStatus = (status) => String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const getStatusInfo = (row, typeKey) => {
  const normalized = normalizeStatus(row.status);
  if (['failed', 'fail', 'error', 'errored'].includes(normalized)) return STATUS_META.failed;
  if (typeKey === 'osint' && ['completed', 'complete', 'done', 'success', 'succeeded'].includes(normalized)) {
    return { ...STATUS_META.complete, label: 'Completed' };
  }
  if (typeKey === 'vishing' && ['done', 'completed', 'complete', 'success', 'succeeded'].includes(normalized)) {
    return { ...STATUS_META.complete, label: 'Done' };
  }
  if (row.risk_score === null || row.risk_score === undefined) return STATUS_META.running;
  return STATUS_META.complete;
};

const getGrade = (score) => {
  if (score === null || score === undefined) return 'N/A';
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return 'N/A';
  if (numeric < 60) return 'F';
  if (numeric < 70) return 'D';
  if (numeric < 80) return 'C';
  if (numeric < 90) return 'B';
  if (numeric <= 95) return 'A-';
  return 'A+';
};

const formatDate = (value) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleDateString();
};

function StatusChip({ status }) {
  const Icon = status.icon;
  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={status.label}
      sx={{
        bgcolor: alpha(status.color, 0.1),
        color: status.color,
        border: `1px solid ${alpha(status.color, 0.18)}`,
        fontWeight: 900,
        '& .MuiChip-icon': { color: status.color },
      }}
    />
  );
}

function TypeChip({ typeKey }) {
  const meta = TYPE_META[typeKey] || TYPE_META.external;
  const Icon = meta.icon;
  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={meta.label}
      sx={{
        bgcolor: alpha(meta.color, 0.1),
        color: meta.color,
        border: `1px solid ${alpha(meta.color, 0.16)}`,
        fontWeight: 900,
        '& .MuiChip-icon': { color: meta.color },
      }}
    />
  );
}

function ReportsHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ai');
  const [pentests, setPentests] = useState([]);
  const [manualReports, setManualReports] = useState([]);
  const [loadingAi, setLoadingAi] = useState(true);
  const [loadingManual, setLoadingManual] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    setLoadingAi(true);
    fetch(`${BASEURL}/pentests`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => setPentests(data.pentests || []))
      .catch(() => setError('Unable to load AI generated reports.'))
      .finally(() => setLoadingAi(false));
  }, []);

  useEffect(() => {
    setLoadingManual(true);
    manualReportsApi.listReports()
      .then(setManualReports)
      .catch(() => setError('Unable to load manual reports.'))
      .finally(() => setLoadingManual(false));
  }, []);

  const aiRows = useMemo(() => (
    pentests.map((row) => {
      const typeKey = normalizePentestType(row.type);
      const status = getStatusInfo(row, typeKey);
      return {
        ...row,
        typeKey,
        status,
        grade: getGrade(row.risk_score),
        reports: reportRoutes[typeKey] || [],
        isReportReady: status.key === 'complete' && Boolean(reportRoutes[typeKey]),
      };
    })
  ), [pentests]);

  const aiReadyCount = aiRows.filter((row) => row.isReportReady).length;
  const manualCount = manualReports.length;

  return (
    <Box className="reports-hub-page">
      <Card className="reports-hero-card">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography className="reports-eyebrow">Reports Center</Typography>
              <Typography className="reports-title">Reports</Typography>
              <Typography className="reports-subtitle">
                Open AI-generated assessment deliverables or build a manual/hybrid report with editable document-style sections.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<ArticleOutlinedIcon />} label={`${aiReadyCount} AI reports ready`} className="reports-hero-chip" />
              <Chip icon={<EditNoteRoundedIcon />} label={`${manualCount} manual reports`} className="reports-hero-chip" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setError('')}>{error}</Alert> : null}

      <Card className="reports-main-card">
        <Box className="reports-tabs-row">
          <Tabs value={activeTab} onChange={(_, nextTab) => setActiveTab(nextTab)}>
            <Tab value="ai" label="AI Generated Reports" icon={<AutoAwesomeRoundedIcon />} iconPosition="start" />
            <Tab value="manual" label="Manual Reports" icon={<EditNoteRoundedIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        <Divider />

        {activeTab === 'ai' ? (
          <Box className="reports-table-panel">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography className="reports-section-title">AI Generated Reports</Typography>
                <Typography className="reports-section-copy">
                  Pick a completed assessment and jump directly to its pentest, recon, attestation, or executive report.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => navigate('/pentests')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}>
                View Pentests
              </Button>
            </Stack>

            {loadingAi ? (
              <Box className="reports-loading"><CircularProgress size={28} /></Box>
            ) : (
              <TableContainer>
                <Table className="reports-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Assessment</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Reports</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aiRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography className="reports-row-title">{row.name || `Pentest ${row.id}`}</Typography>
                          <Typography className="reports-row-subtitle">{row.organization_name || row.details || `ID ${row.id}`}</Typography>
                        </TableCell>
                        <TableCell><TypeChip typeKey={row.typeKey} /></TableCell>
                        <TableCell><StatusChip status={row.status} /></TableCell>
                        <TableCell>
                          <Chip size="small" label={row.typeKey === 'osint' ? 'N/A' : row.grade} sx={{ fontWeight: 950 }} />
                        </TableCell>
                        <TableCell>{formatDate(row.updated_at || row.created_at)}</TableCell>
                        <TableCell>
                          {row.reports.length ? (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {row.reports.map((report) => (
                                <Button
                                  key={report.label}
                                  size="small"
                                  variant={row.isReportReady ? 'contained' : 'outlined'}
                                  disabled={!row.isReportReady}
                                  endIcon={<OpenInNewRoundedIcon />}
                                  onClick={() => navigate(report.path(row.id))}
                                  className="reports-mini-button"
                                >
                                  {report.label}
                                </Button>
                              ))}
                            </Stack>
                          ) : (
                            <Chip size="small" label="No template yet" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : null}

        {activeTab === 'manual' ? (
          <Box className="reports-manual-panel">
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Card className="reports-manual-card">
                  <CardContent>
                    <Typography className="reports-eyebrow">Manual & Hybrid</Typography>
                    <Typography className="reports-section-title">Build reports like a document</Typography>
                    <Typography className="reports-section-copy" sx={{ mt: 1 }}>
                      Use the editable report builder to draft findings, generate polished sections with AI, copy rich text,
                      and export HTML or Word deliverables.
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                      <Button variant="contained" startIcon={<EditNoteRoundedIcon />} onClick={() => navigate('/reports/manual')} className="reports-primary-button">
                        Open Manual Reports
                      </Button>
                      <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => navigate('/reports/manual')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}>
                        Create Report
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card className="reports-manual-card">
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Box>
                        <Typography className="reports-eyebrow">Recent Manual Reports</Typography>
                        <Typography className="reports-section-title">Draft library</Typography>
                      </Box>
                      <Chip label={loadingManual ? 'Loading' : `${manualReports.length} reports`} />
                    </Stack>
                    {loadingManual ? <CircularProgress size={24} /> : null}
                    {!loadingManual && !manualReports.length ? (
                      <Box className="reports-empty-state">No manual reports yet. Create one to start drafting.</Box>
                    ) : null}
                    <Stack spacing={1}>
                      {manualReports.slice(0, 5).map((report) => (
                        <Box key={report.id} className="reports-manual-row">
                          <Box>
                            <Typography className="reports-row-title">{report.report_title || 'Untitled Report'}</Typography>
                            <Typography className="reports-row-subtitle">{report.client_name || 'No client'} - {report.assessment_type || 'Assessment'}</Typography>
                          </Box>
                          <Button size="small" onClick={() => navigate(`/reports/manual/${report.id}`)} endIcon={<OpenInNewRoundedIcon />} className="reports-mini-button">
                            Open
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </Card>
    </Box>
  );
}

export default ReportsHub;
